import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Button, Input, Space, Typography } from "antd";
import type { CustomerAdvancePayment, Page } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table as ZhTable, type TableColumnProps, type TableSortChangeContext } from "zhihao-ui-react";

import { DEFAULT_VALUE, LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { CUSTOMER_ADVANCE_PAY_STATE } from "@/meta/expenseManage/customerAdvancePayment";
import { dateFormat, dateFullTimeFormat } from "@/utils";
import { formatNumber } from "@/utils/number";
import { notifyOperationSuccess } from "@/utils/operationFeedback";
import Permission from "@/views/components/Permission";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useUserStore } from "@/stores/react/authStore";
import request from "@/api";
import { revokeWillPayRecord, type WillPayQuery } from "../../willPay/WillPayPage";
import RevokeDialog from "./revokeDialog";

export interface WillPayRecordProps {
    id: number | string;
    loadRecords?: (query: WillPayQuery & { customerId: number }) => Promise<Page<CustomerAdvancePayment>>;
    revokeRecord?: (params: { id: number | string; remark: string }) => Promise<unknown>;
    onUpdated?: () => void;
}

const formatDate = (value?: string) => (value ? dateFormat(value) : DEFAULT_VALUE);

/** 账户详情沿用预付款分页接口，但保留实体完整字段供账户记录展示。 */
async function loadAccountAdvancePayments(
    query: WillPayQuery & { customerId: number }
): Promise<Page<CustomerAdvancePayment>> {
    const { data } = await request.service.get<Page<CustomerAdvancePayment>>("/customer/advance-payment/paging", {
        params: query
    });
    return data;
}

/** 账户详情的预付款记录，补齐旧 Vue ProTable 的日期筛选、排序、详情和撤销操作。 */
export default function WillPayRecord({
    id,
    loadRecords = loadAccountAdvancePayments,
    revokeRecord = revokeWillPayRecord,
    onUpdated
}: WillPayRecordProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [rows, setRows] = useState<CustomerAdvancePayment[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<WillPayQuery & { customerId: number }>({
        page: 1,
        size: 10,
        customerId: Number(id)
    });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [error, setError] = useState("");
    const [revokeId, setRevokeId] = useState<number | string>();
    const [revokeLoading, setRevokeLoading] = useState(false);

    useEffect(() => {
        const customerId = Number(id);
        // 首次挂载时查询已包含当前账户，避免 React 额外创建查询对象导致 Vue ProTable 的重复请求。
        setQuery(current => (current.customerId === customerId ? current : { ...current, customerId, page: 1 }));
    }, [id]);

    useEffect(() => {
        let disposed = false;
        void loadRecords(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setError("预付款记录加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadRecords, query]);

    // 将旧日期范围转换成接口要求的 T 分隔边界时间，并回到第一页。
    const submitFilters = () => {
        if (startDate && endDate && startDate > endDate) {
            setError("付款日期范围无效");
            return;
        }
        setQuery(current => ({
            ...current,
            page: 1,
            startTime: startDate ? dateFormat(startDate, LOG_DATE_FORMAT_START) : undefined,
            endTime: endDate ? dateFullTimeFormat(endDate, LOG_DATE_FORMAT_END) : undefined
        }));
    };

    const resetFilters = () => {
        setStartDate("");
        setEndDate("");
        setError("");
        setQuery({ page: 1, size: 10, customerId: Number(id) });
    };

    const submitRevoke = async (remark: string) => {
        if (revokeId == null) return;
        setRevokeLoading(true);
        try {
            await revokeRecord({ id: revokeId, remark });
            notifyOperationSuccess("已撤销");
            setRevokeId(undefined);
            setQuery(current => ({ ...current }));
            onUpdated?.();
        } catch {
            setError("预付款撤销失败");
        } finally {
            setRevokeLoading(false);
        }
    };

    const handleSortChange = ({ prop, order }: TableSortChangeContext<CustomerAdvancePayment>) => {
        if (prop !== "date") return;
        setQuery(current => ({
            ...current,
            page: 1,
            sortKey: order ? "date" : undefined,
            sortOrder: order === "ascending" ? "asc" : order === "descending" ? "desc" : undefined
        }));
    };

    const columns: TableColumnProps<CustomerAdvancePayment>[] = [
        {
            key: "id",
            label: "编号",
            minWidth: 300,
            prop: "id",
            render: ({ row }) => (
                <Button type="link" onClick={() => navigate(`/expenseManagement/userAccount/willPay/detail/${row.id}`)}>
                    {row.id}
                </Button>
            )
        },
        {
            key: "amount",
            label: "金额(元)",
            minWidth: 228,
            prop: "amount",
            render: ({ row }) => formatNumber(row.amount, 2)
        },
        {
            key: "date",
            label: "日期",
            minWidth: 214,
            prop: "date",
            sortable: "custom",
            render: ({ row }) => formatDate(row.date)
        },
        {
            key: "state",
            label: "状态",
            minWidth: 214,
            prop: "state",
            render: ({ row }) => CUSTOMER_ADVANCE_PAY_STATE.getIntroByCode(row.state) || DEFAULT_VALUE
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 200,
            prop: "id",
            render: ({ row }) =>
                row.state === CUSTOMER_ADVANCE_PAY_STATE.SUCCESS.code ? (
                    <Permission anyOf={["ROLE_ACCOUNT_MANAGE"]}>
                        <Button disabled={banButton} type="link" onClick={() => setRevokeId(row.id)}>
                            撤销
                        </Button>
                    </Permission>
                ) : null
        }
    ];

    return (
        <div aria-label="user-account-will-pay-record">
            <ZhTable<CustomerAdvancePayment>
                data={rows}
                columns={normalizeOperationColumns(columns, rows)}
                pagination={false}
                rowKey="id"
                onSortChange={handleSortChange}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                            <Typography.Title level={5} className="m-0">
                                预付款记录
                            </Typography.Title>
                            <Permission anyOf={["ROLE_ACCOUNT_MANAGE"]}>
                                <Button
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => navigate(`/expenseManagement/userAccount/willPay/add/${id}`)}
                                >
                                    添加预付款
                                </Button>
                            </Permission>
                        </div>

                        <Space className="mb-4" wrap>
                            <Input
                                aria-label="付款日期起始"
                                placeholder="起始时间"
                                type="date"
                                value={startDate}
                                onChange={event => setStartDate(event.target.value)}
                            />
                            <Input
                                aria-label="付款日期结束"
                                placeholder="结束时间"
                                type="date"
                                value={endDate}
                                onChange={event => setEndDate(event.target.value)}
                            />
                            <Button onClick={submitFilters}>查询</Button>
                            <Button onClick={resetFilters}>重置</Button>
                        </Space>

                        {error ? <Alert className="mb-3" message={error} role="alert" type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            hideOnSinglePage
                            onChange={page => setQuery(current => ({ ...current, page }))}
                        />
                    </div>
                }
            />
            <RevokeDialog
                open={revokeId != null}
                loading={revokeLoading}
                disabled={banButton}
                onCancel={() => setRevokeId(undefined)}
                onConfirm={remark => void submitRevoke(remark)}
            />
        </div>
    );
}
